import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Shield, MessageSquare, TrendingDown, Share2, Lock, Sparkles, Download, RefreshCw, Instagram, Facebook, Twitter, MessageCircle, Activity, ScanLine } from 'lucide-react';
import { useScan } from '../hooks/useScans';
import { motion } from 'framer-motion';
import { trackEvent, trackEventOnce } from '../utils/visitorTracking';
export function PublicResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scanId = searchParams.get('id');
  const {
    scan,
    loading,
    error
  } = useScan(scanId || undefined);
  const [downloading, setDownloading] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  // Only when the completed result has genuinely loaded and rendered — never
  // on a bare mount while the scan is still loading or failed. Keyed on the
  // scan id so Strict Mode or a refresh cannot double-count the view.
  useEffect(() => {
    if (!scan || scan.status !== 'completed') return;
    void trackEventOnce(`result_viewed:${scan.id}`, 'result_viewed', {
      scan_id: scan.id,
      score: scan.score ?? null,
      risk_level: scan.risk_level ?? null,
      source: 'public_results_page'
    });
  }, [scan?.id, scan?.status]);
  const handleDownloadImage = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const {
        toPng
      } = await import('html-to-image');
      const dataUrl = await toPng(cardRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#050505'
      });
      const link = document.createElement('a');
      link.download = `toxicity-result-${scanId}.png`;
      link.href = dataUrl;
      link.click();
      setDownloading(false);
    } catch (err) {
      console.error('Failed to generate image:', err);
      setDownloading(false);
    }
  };
  const handleShareToSocial = async (platform: string) => {
    const shareUrl = window.location.href;
    const text = `I just analyzed my chat on ToxicDetector! 🚩 My toxicity score: ${scan?.score || 0}%`;
    let url = '';
    try {
      switch (platform) {
        case 'twitter':
          url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
          window.open(url, '_blank', 'width=600,height=400');
          break;
        case 'facebook':
          url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
          window.open(url, '_blank', 'width=600,height=400');
          break;
        case 'whatsapp':
          url = `https://wa.me/?text=${encodeURIComponent(text + ' ' + shareUrl)}`;
          window.open(url, '_blank', 'width=600,height=400');
          break;
        case 'instagram':
        case 'tiktok':
          const fullText = `${text}\n\n${shareUrl}`;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(fullText);
            const platformName = platform === 'instagram' ? 'Instagram' : 'TikTok';
            alert(`✓ Link copied! Open ${platformName} and paste it in your story or post.`);
          }
          break;
      }
      // Fires only after the share window opened or the link was copied.
      void trackEvent('result_shared', {
        scan_id: scanId,
        platform,
        method: ['instagram', 'tiktok'].includes(platform) ? 'copy_link' : 'web_intent',
        score: scan?.score ?? null,
        source: 'public_results_page'
      });
      setShowShareMenu(false);
    } catch (err) {
      console.error('Share error:', err);
      alert('Failed to copy link. Please copy manually: ' + shareUrl);
    }
  };
  if (!scanId) {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white" data-id="element-9698" data-mp="1943t8wv1ydna">
        <header className="bg-white/5 backdrop-blur-md border-b border-white/10" data-id="element-9699" data-mp="25txo6p9i5zaw">
          <div className="max-w-4xl mx-auto px-5 py-4 flex items-center gap-2" data-id="element-9700" data-mp="20wo0be1rhvp6">
            <Activity className="text-red-500" data-id="element-9701" data-mp="2fkis8s7y3w17" />
            <h1 className="text-xl font-black tracking-tight" data-id="element-9702" data-mp="2fk9316uq5swk">ToxicDetector</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-5" data-id="element-9703" data-mp="2amh8e52p3soo">
          <div className="text-center" data-id="element-9704" data-mp="1mwweir507b0m">
            <p className="text-white/60 mb-6 font-mono" data-id="element-9705" data-mp="1mxy21v37hpz8">No scan ID provided</p>
            <Link to="/" className="inline-block bg-white text-black px-8 py-3 rounded-xl font-bold hover:bg-gray-200 transition-all" data-id="element-9706" data-mp="1hyux0an87jr9">
              Go to Home
            </Link>
          </div>
        </div>
      </div>;
  }
  if (loading) {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white" data-id="element-9707" data-mp="37a55gz7i8kfq">
        <header className="bg-white/5 backdrop-blur-md border-b border-white/10" data-id="element-9708" data-mp="339l93xur9kun">
          <div className="max-w-4xl mx-auto px-5 py-4 flex items-center gap-2" data-id="element-9709" data-mp="3asui3pzcnhj6">
            <Activity className="text-red-500" data-id="element-9710" data-mp="3fti7y8budsbt" />
            <h1 className="text-xl font-black tracking-tight" data-id="element-9711" data-mp="3fua35i1j1o1k">ToxicDetector</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-5" data-id="element-9712" data-mp="30xsuwzomwpj8">
          <div className="text-center" data-id="element-9713" data-mp="35vkl9p9ms3lb">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent mx-auto mb-6" data-id="element-9714" data-mp="35wm5okcje2fp"></div>
            <p className="text-white/60 font-mono uppercase tracking-widest" data-id="element-9715" data-mp="2elyv8ipw33o">
              Analyzing Data...
            </p>
          </div>
        </div>
      </div>;
  }
  if (error || !scan) {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white" data-id="element-9716" data-mp="3oslrteiu2brd">
        <header className="bg-white/5 backdrop-blur-md border-b border-white/10" data-id="element-9717" data-mp="gdbsge6bvkna">
          <div className="max-w-4xl mx-auto px-5 py-4 flex items-center gap-2" data-id="element-9718" data-mp="l4abhlipawu8">
            <Activity className="text-red-500" data-id="element-9719" data-mp="l6eqayi51g9a" />
            <h1 className="text-xl font-black tracking-tight" data-id="element-9720" data-mp="uzhjdpwtklsb">ToxicDetector</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-5" data-id="element-9721" data-mp="q2r3p3oxfs7f">
          <div className="text-center" data-id="element-9722" data-mp="lzclq7hfh1rc">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" data-id="element-9723" data-mp="lyb2vgu67az2" />
            <p className="text-red-400 mb-6 font-mono" data-id="element-9724" data-mp="gzq1he36ml91">
              {error || 'Results not found'}
            </p>
            <Link to="/" className="inline-block bg-white text-black px-8 py-3 rounded-xl font-bold hover:bg-gray-200 transition-all" data-id="element-9725" data-mp="c399g8sv3mqo">
              Go to Home
            </Link>
          </div>
        </div>
      </div>;
  }
  const score = scan.score || 0;
  const riskLevel = scan.risk_level || 'low';
  const patterns = scan.patterns || [];
  const isPaid = scan.is_unlocked || false;
  const getWittyVerdict = (score: number): string => {
    if (score >= 85) return "Girl, RUN. That's manipulation 101. 🏃‍♀️";
    if (score >= 70) return 'Major red flags detected. Trust your gut. 🚩';
    if (score >= 50) return "Proceed with caution. Something's off here. ⚠️";
    if (score >= 30) return 'Not terrible, but keep your eyes open. 👀';
    return 'Looking pretty healthy! Keep it that way. ✨';
  };
  const getThemeColors = (level: string) => {
    if (level === 'high') return {
      text: 'text-red-500',
      bg: 'bg-red-500',
      glow: 'bg-red-500/20',
      border: 'border-red-500/30'
    };
    if (level === 'medium') return {
      text: 'text-amber-500',
      bg: 'bg-amber-500',
      glow: 'bg-amber-500/20',
      border: 'border-amber-500/30'
    };
    return {
      text: 'text-emerald-500',
      bg: 'bg-emerald-500',
      glow: 'bg-emerald-500/20',
      border: 'border-emerald-500/30'
    };
  };
  const theme = getThemeColors(riskLevel);
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white relative overflow-hidden" data-id="element-9726" data-mp="12jtl7viaquze">
      {/* Background Glow */}
      <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full ${theme.glow} blur-[120px] mix-blend-screen opacity-40 pointer-events-none`} data-id="element-9727" data-mp="3s2wrquhhmngp" />

      {/* Header */}
      <header className="bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50" data-id="element-9728" data-mp="5wi05d3y26cb">
        <div className="max-w-4xl mx-auto px-5 py-4" data-id="element-9729" data-mp="u96sfn5t29o">
          <div className="flex items-center justify-between" data-id="element-9730" data-mp="ev3gd1ly199x">
            <div className="flex items-center gap-2" data-id="element-9731" data-mp="etijv6g2jg9f">
              <Activity className={theme.text} data-id="element-9732" data-mp="9vh34s647t14" />
              <h1 className="text-xl font-black tracking-tight" data-id="element-9733" data-mp="9uo4yfl82bul">
                ToxicDetector
              </h1>
            </div>
            <Link to="/signup" className="text-white/60 text-sm font-bold hover:text-white transition-colors" data-id="element-9734" data-mp="jls7ugxmi692">
              Try it yourself
            </Link>
          </div>
        </div>
      </header>

      <div className="flex-1 w-full max-w-2xl mx-auto px-4 py-8 relative z-10" data-id="element-9735" data-mp="tm2n0737xnqv">
        {!isPaid ? <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-9736" data-mp="1c9l6ir7skvkv">
            {/* Teaser Card */}
            <div className="bg-white/5 backdrop-blur-xl rounded-[32px] p-8 md:p-12 border border-white/10 shadow-2xl mb-8 relative overflow-hidden text-center" data-id="element-9737" data-mp="185djcygzfnor">
              <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-9738" data-mp="ya2b8z02xepz" />

              <div className="w-20 h-20 mx-auto bg-white/10 rounded-2xl flex items-center justify-center mb-8 border border-white/10" data-id="element-9739" data-mp="kjgq13n5ksox">
                <Lock className="w-10 h-10 text-white/50" data-id="element-9740" data-mp="ajpvj1yledou" />
              </div>

              <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-4" data-id="element-9741" data-mp="alskdio0e0rz">
                Results Locked
              </h2>
              <p className="text-white/60 font-mono text-sm mb-10 max-w-sm mx-auto" data-id="element-9742" data-mp="1hseg7io346yq">
                We've analyzed {scan.message_count} messages and found potential
                patterns of manipulation.
              </p>

              {/* Blurred Preview */}
              <div className="relative mb-10" data-id="element-9743" data-mp="1ctjry17clq78">
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent z-10" data-id="element-9744" data-mp="1cucqiew5l9ea" />
                <div className="filter blur-md opacity-50 select-none" data-id="element-9745" data-mp="20jxjlqm0tn1a">
                  <div className="text-8xl font-black text-white mb-4" data-id="element-9746" data-mp="25h79t8h8i2q6">84</div>
                  <div className="h-24 bg-white/10 rounded-xl mx-auto max-w-xs mb-4" data-id="element-9747" data-mp="1qtug2wh9wqop" />
                  <div className="h-24 bg-white/10 rounded-xl mx-auto max-w-xs" data-id="element-9748" data-mp="1vs5ttiaovlc7" />
                </div>
              </div>

              <Link to="/payment" className="block w-full bg-white text-black text-lg font-black py-5 rounded-2xl hover:bg-gray-200 transition-all shadow-lg hover:scale-[1.02] active:scale-[0.98]" data-id="element-9749" data-mp="2jgysvnmly9b1">
                Unlock Full Report — $2.99
              </Link>
              <p className="text-white/40 text-xs font-mono mt-4 uppercase tracking-widest" data-id="element-9750" data-mp="7tv9g9mbnai6">
                One-time payment • Instant access
              </p>
            </div>
          </motion.div> : <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-9751" data-mp="hkznn746arby">
            {/* Main Result Card - Shareable */}
            <div ref={cardRef} className="bg-[#050505] rounded-[32px] p-8 md:p-12 border border-white/10 shadow-2xl mb-8 relative overflow-hidden" data-id="element-9752" data-mp="2t78av4dpda8">
              <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full ${theme.glow} blur-[80px] mix-blend-screen opacity-50 pointer-events-none`} data-id="element-9753" data-mp="1ecthwi3f5lzo" />

              <div className="relative z-10" data-id="element-9754" data-mp="70kaduk880mr">
                {/* Header */}
                <div className="flex items-center justify-between mb-12" data-id="element-9755" data-mp="bvhkz5x5mi1r">
                  <div className="flex items-center gap-3" data-id="element-9756" data-mp="g07faqc5zokk">
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center" data-id="element-9757" data-mp="kxqtxrcry3d1">
                      <ScanLine className="w-5 h-5 text-white" data-id="element-9758" data-mp="3ke5kr8yrj8an" />
                    </div>
                    <span className="font-black text-white tracking-tight uppercase" data-id="element-9759" data-mp="3odxx7wra1yla">
                      ToxicDetector
                    </span>
                  </div>
                  <span className="text-xs font-mono text-white/50 uppercase tracking-widest" data-id="element-9760" data-mp="1z4s2a9fo882">
                    {new Date().toLocaleDateString()}
                  </span>
                </div>

                {/* Verdict */}
                <div className="text-center mb-12" data-id="element-9761" data-mp="35ng4vtacx50g">
                  <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight leading-tight" data-id="element-9762" data-mp="35m5667vrqng7">
                    {getWittyVerdict(score)}
                  </h2>
                </div>

                {/* Score */}
                <div className="flex flex-col items-center justify-center mb-12" data-id="element-9763" data-mp="2o69adlfafpn0">
                  <div className="relative w-48 h-48 flex items-center justify-center mb-6" data-id="element-9764" data-mp="2k4xsy36kiapw">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 200 200" data-id="element-9765" data-mp="2k7a6831drwwm">
                      <circle cx="100" cy="100" r="88" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="12" strokeLinecap="round" data-id="element-9766" data-mp="2aecmxuoke3wy" />
                      <circle cx="100" cy="100" r="88" fill="none" stroke="currentColor" className={theme.text} strokeWidth="12" strokeLinecap="round" strokeDasharray={2 * Math.PI * 88} strokeDashoffset={2 * Math.PI * 88 - score / 100 * (2 * Math.PI * 88)} data-id="element-9767" data-mp="1whfzm37nn4kt" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-9768" data-mp="37wmyh8uzqhwp">
                      <span className="text-6xl font-black text-white tracking-tighter" data-id="element-9769" data-mp="3criwqqqtm1i9">
                        {score}
                      </span>
                    </div>
                  </div>
                  <div className={`inline-flex items-center gap-2 px-6 py-2 rounded-full bg-white/5 border ${theme.border} backdrop-blur-md`} data-id="element-9770" data-mp="33v1ihen5tnia">
                    <span className={`text-sm font-black ${theme.text} uppercase tracking-widest`} data-id="element-9771" data-mp="120srpzmmche5">
                      {riskLevel} Risk
                    </span>
                  </div>
                </div>

                {/* Patterns */}
                {patterns.length > 0 && <div className="space-y-4 mb-12" data-id="element-9772" data-mp="1g745dnitltqz">
                    {patterns.slice(0, 3).map((pattern: any, index) => <div key={index} className="bg-white/5 backdrop-blur-md rounded-2xl p-5 border border-white/10 flex items-start gap-4" data-id="element-9773" data-mp="1uv7dp3kc5hiy" data-mpi={index}>
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0" data-id="element-9774" data-mp="23vneg59p2ak4">
                          {getPatternIcon(pattern.type)}
                        </div>
                        <div data-id="element-9775" data-mp="9jfdobaoyzgf">
                          <div className="flex items-center gap-3 mb-1" data-id="element-9776" data-mp="9kqimefvid94">
                            <h4 className="font-bold text-white text-lg" data-id="element-9777" data-mp="5in41gxwqsjm">
                              {pattern.type}
                            </h4>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-widest border ${getPatternColorClass(pattern.severity)}`} data-id="element-9778" data-mp="29kn9hllbdzdf">
                              {pattern.severity}
                            </span>
                          </div>
                          <p className="text-white/60 text-sm leading-relaxed" data-id="element-9779" data-mp="2njwoihxsanft">
                            {pattern.description}
                          </p>
                        </div>
                      </div>)}
                  </div>}

                {/* Footer Stats */}
                <div className="flex items-center justify-between pt-6 border-t border-white/10" data-id="element-9780" data-mp="328svscs0nwfd">
                  <div className="flex gap-8" data-id="element-9781" data-mp="37ai8dwuw0i5k">
                    <div data-id="element-9782" data-mp="3b9r4v9hbau4t">
                      <div className="text-2xl font-black text-white" data-id="element-9783" data-mp="3bb25x1jdhns8">
                        {scan.message_count}
                      </div>
                      <div className="text-[10px] text-white/40 font-mono uppercase tracking-widest" data-id="element-9784" data-mp="zkkn8o5pyvw5">
                        Messages
                      </div>
                    </div>
                    <div data-id="element-9785" data-mp="qobnf8dtxv9h">
                      <div className="text-2xl font-black text-white" data-id="element-9786" data-mp="lne60y0n9591">
                        {patterns.length}
                      </div>
                      <div className="text-[10px] text-white/40 font-mono uppercase tracking-widest" data-id="element-9787" data-mp="1j9lam7wy6xle">
                        Patterns
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-9788" data-mp="19jievzyfz5jd">
                    <Activity className={`w-4 h-4 ${theme.text}`} data-id="element-9789" data-mp="14ms33oto6w3r" />
                    <span className="text-sm font-black text-white tracking-tight" data-id="element-9790" data-mp="14k5sthfea2hu">
                      ToxicDetector.ai
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 mb-12" data-id="element-9791" data-mp="15ljm3omg9c62">
              <button onClick={handleDownloadImage} disabled={downloading} className="w-full bg-white text-black text-lg font-black py-4 rounded-2xl hover:bg-gray-200 transition-all shadow-lg flex items-center justify-center gap-3 disabled:opacity-50" data-id="element-9792" data-mp="1adjfey80l6to">
                {downloading ? <>
                    <RefreshCw className="animate-spin" data-id="element-9793" data-mp="1obri7mrqf2cr" /> Generating...
                  </> : <>
                    <Download size={20} data-id="element-9794" data-mp="1fdflr446pace" /> Save Image
                  </>}
              </button>

              <button onClick={() => setShowShareMenu(!showShareMenu)} className="w-full bg-white/10 text-white text-lg font-black py-4 rounded-2xl hover:bg-white/20 transition-all border border-white/20 flex items-center justify-center gap-3" data-id="element-9795" data-mp="1kaxv2vm45rkx">
                <Share2 size={20} data-id="element-9796" data-mp="f1tk71f0gi6" /> Share Verdict
              </button>

              {showShareMenu && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 border border-white/10" data-id="element-9797" data-mp="3siklhijdlfma">
                  <div className="grid grid-cols-2 gap-3" data-id="element-9798" data-mp="38vmssw48vv83">
                    <button onClick={() => handleShareToSocial('instagram')} className="bg-gradient-to-br from-purple-500 to-pink-500 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2" data-id="element-9799" data-mp="38xg44opsz3mf">
                      <Instagram size={18} data-id="element-9800" data-mp="2v36qjxj64188" /> Instagram
                    </button>
                    <button onClick={() => handleShareToSocial('tiktok')} className="bg-black text-white py-3 rounded-xl font-bold border border-white/20 flex items-center justify-center gap-2" data-id="element-9801" data-mp="2q5x2ombffxmi">
                      <MessageCircle size={18} data-id="element-9802" data-mp="1hk1hmiuu3xsy" /> TikTok
                    </button>
                    <button onClick={() => handleShareToSocial('twitter')} className="bg-blue-500 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2" data-id="element-9803" data-mp="1wcv8f0fk40cx">
                      <Twitter size={18} data-id="element-9804" data-mp="20cvlagn0c3hv" /> Twitter
                    </button>
                    <button onClick={() => handleShareToSocial('whatsapp')} className="bg-green-500 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2" data-id="element-9805" data-mp="2f9mmmvg8rpa4">
                      <MessageCircle size={18} data-id="element-9806" data-mp="kwn52wsawl07" /> WhatsApp
                    </button>
                  </div>
                </motion.div>}

              <Link to="/upload" className="block w-full text-white/60 text-sm font-bold py-4 text-center hover:text-white transition-colors uppercase tracking-widest" data-id="element-9807" data-mp="2rjvsxtxva61o">
                Scan Another Chat
              </Link>
            </div>
          </motion.div>}
      </div>
    </div>;
}
// Helper functions
function getPatternIcon(type: string): React.ReactNode {
  const iconMap: Record<string, React.ReactNode> = {
    Gaslighting: <AlertTriangle size={20} className="text-white" data-id="element-9808" data-mp="28vqv133w5fas" />,
    Manipulation: <Shield size={20} className="text-white" data-id="element-9809" data-mp="2cwj51ykl7o84" />,
    Dismissiveness: <MessageSquare size={20} className="text-white" data-id="element-9810" data-mp="1yvq5v0jcc4yo" />,
    'Blame Shifting': <TrendingDown size={20} className="text-white" data-id="element-9811" data-mp="23u9vpc1220wb" />,
    'Emotional Abuse': <AlertTriangle size={20} className="text-white" data-id="element-9812" data-mp="23ujlozm3o820" />
  };
  return iconMap[type] || <AlertTriangle size={20} className="text-white" data-id="element-9813" data-mp="3taj1v0o4yxr5" />;
}
function getPatternColorClass(severity: string): string {
  return severity === 'HIGH' ? 'text-red-400 border-red-400/30 bg-red-400/10' : severity === 'MEDIUM' ? 'text-amber-400 border-amber-400/30 bg-amber-400/10' : 'text-emerald-400 border-emerald-400/30 bg-emerald-400/10';
}